/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function Countries() {
    const [countries, setCountries] = useState([]);
    const [newRecord, setNewRecord] = useState({ name: "", iso_code: "", phone_code: "", currency: "" });
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countryToDelete, setCountryToDelete] = useState(null);
    const formRef = useRef(null);

    // React Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Filter countries based on search term
    const filteredCountries = countries.filter(country =>
        country.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.iso_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.phone_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.currency?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort filtered countries
    const sortedCountries = React.useMemo(() => {
        let sortableCountries = [...filteredCountries];
        if (sortConfig.key) {
            sortableCountries.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableCountries;
    }, [filteredCountries, sortConfig]);

    // Calculate pagination
    const totalItems = sortedCountries.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentCountries = sortedCountries.slice(startIndex, endIndex);

    // Handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort icon
    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? '↑' : '↓';
        }
        return '';
    };

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Fetch Countries
    const fetchCountries = async () => {
        try {
            const response = await axiosInstance.get("/country/");
            setCountries(response.data.data);
            console.log("Countries", response.data.data);
        } catch (error) {
            console.error("Error fetching Countries:", error);
        }
    };

    // Fetch Countries on component mount
    useEffect(() => {
        fetchCountries();
    }, []);

    // Open Edit Modal and Set Data
    const handleEditClick = (country) => {
        setSelectedCountry(country);
        setNewRecord({
            name: country.name || "",
            iso_code: country.iso_code || "",
            phone_code: country.phone_code || "",
            currency: country.currency || ""
        });
    };

    // Handle Form Input Change
    const handleChange = (e) => {
        setNewRecord({ ...newRecord, [e.target.name]: e.target.value });
    };

    // Reset form when modals are closed
    const resetForm = () => {
        setNewRecord({ name: "", iso_code: "", phone_code: "", currency: "" });
        setSelectedCountry(null);
        if (formRef.current) {
            formRef.current.classList.remove("was-validated");
        }
    };

    // Handle Update Country
    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedCountry) return;

        try {
            await axiosInstance.put(`/country/${selectedCountry.id}`, newRecord);

            setCountries(prevCountries =>
                prevCountries.map(c =>
                    c.id === selectedCountry.id ? { ...c, ...newRecord } : c
                )
            );

            Swal.fire({
                title: "Updated!",
                text: "Country updated successfully!",
                icon: "success"
            }).then(() => {
                $("#edit_country").modal("hide");
            });

            resetForm();
        } catch (error) {
            console.error("Error updating country:", error);
            Swal.fire("Error", "Failed to update country", "error");
        }
    };

    // Handle Delete Click
    const handleDeleteClick = (country) => {
        setCountryToDelete(country);
    };

    // Handle Delete Country
    const confirmDelete = async () => {
        if (!countryToDelete) return;

        try {
            await axiosInstance.delete(`/country/${countryToDelete.id}`);

            setCountries(prevCountries =>
                prevCountries.filter(c => c.id !== countryToDelete.id)
            );

            Swal.fire({
                title: "Deleted!",
                text: "Country deleted successfully!",
                icon: "success"
            }).then(() => {
                $("#delete_country").modal("hide");
            });

            setCountryToDelete(null);
        } catch (error) {
            console.error("Error deleting country:", error);
            Swal.fire("Error", "Failed to delete country", "error");
        }
    };

    // Handle Form Submission (Create)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formRef.current.checkValidity()) {
            formRef.current.classList.add("was-validated");
            return;
        }

        try {
            const payload = {
                ...newRecord,
                created_by: localStorage.getItem("user_id")
            };

            const { data } = await axiosInstance.post("/country", payload);
            console.log("backend:", data.data);

            setCountries(prev => [...prev, data.data]);
            Swal.fire({
                title: "Created!",
                text: "The Country record has been created.",
                icon: "success"
            }).then(() => {
                $("#add_countries").modal("hide");
            });

            resetForm();
        } catch (error) {
            console.error("Error adding country:", error);
            Swal.fire("Error", "Failed to create country", "error");
        }
    };

    // Pagination component
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    {filteredCountries.length !== countries.length && ` (filtered from ${countries.length} total entries)`}
                </div>
                <div className="d-flex align-items-center">
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> First
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> Previous
                                </button>
                            </li>
                            {pageNumbers.map(number => (
                                <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(number)}
                                    >
                                        {number}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next <i className="fa fa-angle-double-right ms-2"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header ">
                            <h5>Countries</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <a className="btn btn-filters w-auto popup-toggle" data-bs-toggle="tooltip" data-bs-placement="bottom" data-bs-original-title="Filter"><span className="me-2"><img src="assets/img/icons/filter-icon.svg" alt="filter" /></span>Filter </a>
                                    </li>
                                    <li className="">
                                        <div className="dropdown dropdown-action" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Download">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false"><span><i className="fe fe-download"></i></span></a>
                                            <div className="dropdown-menu dropdown-menu-right">
                                                <ul className="d-block">
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);" download><i className="far fa-file-pdf me-2"></i>PDF</a>
                                                    </li>
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);" download><i className="far fa-file-text me-2"></i>CVS</a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn-filters" href="javascript:void(0);" data-bs-toggle="tooltip" data-bs-placement="bottom" data-bs-original-title="Print"><span><i className="fe fe-printer"></i></span> </a>
                                    </li>
                                    <li>
                                        <a className="btn btn-primary" href="#" data-bs-toggle="modal" data-bs-target="#add_countries"><i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add Country</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* All Invoice */}
                    <div className="card invoices-tabs-card">
                        <div className="invoices-main-tabs">
                            <div className="row align-items-center">
                                <div className="col-lg-12">
                                    <div className="invoices-tabs">
                                        <ul>
                                            <li><a href="/countries" className="active">Country</a></li>
                                            <li><a href="/states">State</a></li>
                                            <li><a href="/cities">City</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /All Invoice */}

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    {/* Table Controls */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }}
                                                placeholder="Search countries..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('name')}
                                                    >
                                                        Name {getSortIcon('name')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('iso_code')}
                                                    >
                                                        ISO Code {getSortIcon('iso_code')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('phone_code')}
                                                    >
                                                        Phone Code {getSortIcon('phone_code')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('currency')}
                                                    >
                                                        Currency {getSortIcon('currency')}
                                                    </th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentCountries.length > 0 ? (
                                                    currentCountries.map((country, index) => (
                                                        <tr key={country.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>{country.name}</td>
                                                            <td>{country.iso_code}</td>
                                                            <td>{country.phone_code}</td>
                                                            <td>{country.currency}</td>
                                                            <td className="d-flex align-items-center">
                                                                <button
                                                                    className="btn btn-greys bg-success-light me-2"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#edit_country"
                                                                    onClick={() => handleEditClick(country)}
                                                                >
                                                                    <i className="fa fa-edit me-1"></i>Edit
                                                                </button>
                                                                <button
                                                                    className="btn btn-greys bg-danger-light me-2"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#delete_country"
                                                                    onClick={() => handleDeleteClick(country)}
                                                                >
                                                                    <i className="fa fa-trash-alt me-1"></i> Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">
                                                            {searchTerm ? "No matching countries found" : "No countries available in table"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Table */}
                </div>
                <Footer />
            </div>
            {/* /Page Wrapper */}

            {/* Add Countries Modal */}
            <div className="modal custom-modal fade" id="add_countries" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0">Add Country</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={resetForm}>
                            </button>
                        </div>
                        <form ref={formRef} onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={newRecord.name}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter Name"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>ISO Code <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                name="iso_code"
                                                value={newRecord.iso_code}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter ISO Code"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Phone Code <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                name="phone_code"
                                                value={newRecord.phone_code}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter Phone Code"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Currency <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                name="currency"
                                                value={newRecord.currency}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter Currency"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2" onClick={resetForm}>Cancel</button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">Add Country</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {/* /Add Countries Modal */}

            {/* Edit Country Modal */}
            <div className="modal custom-modal fade" id="edit_country" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0">Edit Country</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={resetForm}>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={newRecord.name}
                                            onChange={handleChange}
                                            placeholder="Enter Name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>ISO Code <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="iso_code"
                                            value={newRecord.iso_code}
                                            onChange={handleChange}
                                            placeholder="Enter ISO Code"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Phone Code <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone_code"
                                            value={newRecord.phone_code}
                                            onChange={handleChange}
                                            placeholder="Enter Phone Code"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Currency <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="currency"
                                            value={newRecord.currency}
                                            onChange={handleChange}
                                            placeholder="Enter Currency"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2" onClick={resetForm}>Cancel</button>
                            <button type="button" onClick={handleUpdate} className="btn btn-primary paid-continue-btn">Update</button>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Edit Country Modal */}

            {/* Delete Country Modal */}
            <div className="modal custom-modal fade" id="delete_country" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className="form-header">
                                <h3>Delete Country</h3>
                                <p>Are you sure want to delete "{countryToDelete?.name}"?</p>
                            </div>
                            <div className="modal-btn delete-action">
                                <div className="row">
                                    <div className="col-6">
                                        <button type="button" className="w-100 btn btn-primary paid-continue-btn" onClick={confirmDelete}>Delete</button>
                                    </div>
                                    <div className="col-6">
                                        <button type="button" data-bs-dismiss="modal" className="w-100 btn btn-primary paid-cancel-btn">Cancel</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Delete Country Modal */}
        </div>
    )
}

export default Countries